'use client';
import React, { useState, useEffect } from 'react';
import AppLayout from '@/components/AppLayout';
import Icon from '@/components/ui/AppIcon';
import Modal from '@/components/ui/Modal';
import { createClient } from '@/lib/supabase/client';

interface Vendor {
  id: string;
  name: string;
  initials: string;
  category: string;
  status: 'available' | 'busy' | 'no-response' | 'blacklisted';
  responseSpeed: number;
  winRate: number;
  totalRFQs: number;
  lastActive: string;
  specialties: string[];
  location: string;
  rfqSent?: boolean;
}

const STATUS_CONFIG = {
  available: { color: '#00D68F', bg: 'rgba(0,214,143,0.12)', label: '🟢 Available', glow: 'rgba(0,214,143,0.4)' },
  busy: { color: '#F59E0B', bg: 'rgba(245,158,11,0.12)', label: '🟡 Busy', glow: 'rgba(245,158,11,0.3)' },
  'no-response': { color: '#FF4757', bg: 'rgba(255,71,87,0.1)', label: '🔴 No Response', glow: 'rgba(255,71,87,0.3)' },
  blacklisted: { color: '#4A5568', bg: 'rgba(74,85,104,0.1)', label: '⚫ Blacklisted', glow: 'rgba(74,85,104,0.2)' },
};

function timeAgo(dateStr: string | null): string {
  if (!dateStr) return 'Unknown';
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  const hours = Math.floor(mins / 60);
  const days = Math.floor(hours / 24);
  if (days > 0) return `${days}d ago`;
  if (hours > 0) return `${hours}h ago`;
  if (mins > 0) return `${mins}m ago`;
  return 'Just now';
}

function StarRating({ value }: { value: number }) {
  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map(i => (
        <div key={i} className="w-2 h-2 rounded-sm" style={{ background: i <= Math.round(value) ? '#F59E0B' : 'rgba(128,128,128,0.2)' }} />
      ))}
      <span className="text-[9px] ml-1 font-600" style={{ color: '#F59E0B' }}>{value}</span>
    </div>
  );
}

function VendorCard({ vendor, onScramble }: { vendor: Vendor; onScramble: (id: string) => void }) {
  const [scrambled, setScrambled] = useState(vendor.rfqSent || false);
  const sc = STATUS_CONFIG[vendor.status];
  const isBlacklisted = vendor.status === 'blacklisted';

  return (
    <div className="rounded-xl p-4 transition-all duration-200" style={{ background: 'var(--card)', border: `1px solid ${isBlacklisted ? 'rgba(74,85,104,0.2)' : 'rgba(30,144,255,0.15)'}`, opacity: isBlacklisted ? 0.6 : 1 }}>
      <div className="flex items-start gap-3 mb-3">
        <div className="w-10 h-10 rounded-xl flex items-center justify-center text-white text-sm font-700 shrink-0" style={{ background: isBlacklisted ? 'rgba(74,85,104,0.3)' : 'linear-gradient(135deg, #1E90FF, #0066CC)', boxShadow: isBlacklisted ? 'none' : '0 0 12px rgba(30,144,255,0.3)' }}>{vendor.initials}</div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-700 truncate" style={{ color: 'var(--card-foreground)' }}>{vendor.name}</p>
          <p className="text-[10px] mt-0.5" style={{ color: 'var(--muted-foreground)' }}>{vendor.category}</p>
          <p className="text-[9px] mt-0.5" style={{ color: 'var(--muted-foreground)' }}>📍 {vendor.location}</p>
        </div>
        <div className="text-[9px] font-600 px-2 py-1 rounded-lg shrink-0" style={{ background: sc.bg, color: sc.color, border: `1px solid ${sc.color}30` }}>{sc.label}</div>
      </div>
      <div className="grid grid-cols-3 gap-2 mb-3">
        <div className="text-center p-1.5 rounded-lg" style={{ background: 'rgba(30,144,255,0.06)' }}>
          <p className="text-sm font-700" style={{ color: 'var(--card-foreground)' }}>{vendor.winRate}%</p>
          <p className="text-[9px]" style={{ color: 'var(--muted-foreground)' }}>Win Rate</p>
        </div>
        <div className="text-center p-1.5 rounded-lg" style={{ background: 'rgba(30,144,255,0.06)' }}>
          <p className="text-sm font-700" style={{ color: 'var(--card-foreground)' }}>{vendor.totalRFQs}</p>
          <p className="text-[9px]" style={{ color: 'var(--muted-foreground)' }}>RFQs</p>
        </div>
        <div className="text-center p-1.5 rounded-lg" style={{ background: 'rgba(30,144,255,0.06)' }}>
          <p className="text-[9px] font-600" style={{ color: 'var(--card-foreground)' }}>{vendor.lastActive}</p>
          <p className="text-[9px]" style={{ color: 'var(--muted-foreground)' }}>Last Active</p>
        </div>
      </div>
      <div className="mb-3">
        <div className="flex items-center justify-between mb-1">
          <span className="text-[9px] uppercase tracking-wide" style={{ color: 'var(--muted-foreground)' }}>Response Speed</span>
        </div>
        <StarRating value={vendor.responseSpeed} />
      </div>
      <div className="flex flex-wrap gap-1 mb-3">
        {vendor.specialties?.map(s => (
          <span key={s} className="text-[9px] font-600 px-1.5 py-0.5 rounded" style={{ background: 'rgba(30,144,255,0.1)', color: '#1E90FF', border: '1px solid rgba(30,144,255,0.2)' }}>{s}</span>
        ))}
      </div>
      {!isBlacklisted && (
        <button
          onClick={() => { setScrambled(true); onScramble(vendor.id); }}
          disabled={scrambled}
          className={`w-full py-2 rounded-lg text-xs font-700 tracking-widest uppercase transition-all duration-300 ${scrambled ? 'scramble-btn scramble-sent' : 'scramble-btn'}`}
        >
          {scrambled ? '✈️ RFQ SENT' : '⚡ SCRAMBLE'}
        </button>
      )}
      {isBlacklisted && (
        <div className="w-full py-2 rounded-lg text-xs font-600 text-center" style={{ background: 'rgba(74,85,104,0.2)', color: '#4A5568' }}>⛔ BLACKLISTED</div>
      )}
    </div>
  );
}

const EMPTY_VENDOR = { name: '', category: 'Networking', location: '', status: 'available', specialties: '' };

export default function VendorManagementPage() {
  const [filter, setFilter] = useState<'all' | 'available' | 'busy' | 'no-response'>('all');
  const [vendors, setVendors] = useState<Vendor[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showScrambleAllModal, setShowScrambleAllModal] = useState(false);
  const [formData, setFormData] = useState(EMPTY_VENDOR);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [scrambledAll, setScrambledAll] = useState(false);
  const [scramblingAll, setScramblingAll] = useState(false);

  const fetchVendors = async () => {
    const supabase = createClient();
    try {
      const { data, error } = await supabase
        .from('vendors')
        .select('id, name, initials, category, status, response_speed, win_rate, total_rfqs, last_active_at, specialties, location')
        .order('name', { ascending: true });

      if (error) { console.error('Error fetching vendors:', error.message); return; }

      const mapped: Vendor[] = (data || []).map((v: any) => ({
        id: v.id,
        name: v.name,
        initials: v.initials,
        category: v.category,
        status: v.status as Vendor['status'],
        responseSpeed: Number(v.response_speed),
        winRate: v.win_rate,
        totalRFQs: v.total_rfqs,
        lastActive: timeAgo(v.last_active_at),
        specialties: v.specialties || [],
        location: v.location,
      }));
      setVendors(mapped);
    } catch (err) {
      console.error('Unexpected error:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchVendors(); }, []);

  const handleAddVendor = async () => {
    if (!formData.name.trim()) { setSaveError('Vendor name is required.'); return; }
    setSaving(true);
    setSaveError('');
    const supabase = createClient();
    try {
      const initials = formData.name.split(' ').map((n: string) => n[0]).join('').slice(0, 2).toUpperCase();
      const specialtiesArr = formData.specialties ? formData.specialties.split(',').map(s => s.trim()).filter(Boolean) : [];
      const { error } = await supabase.from('vendors').insert({
        name: formData.name,
        initials,
        category: formData.category,
        location: formData.location || 'Unknown',
        status: formData.status,
        response_speed: 3,
        win_rate: 0,
        total_rfqs: 0,
        specialties: specialtiesArr,
        last_active_at: new Date().toISOString(),
      });
      if (error) { setSaveError(error.message); return; }
      setShowAddModal(false);
      setFormData(EMPTY_VENDOR);
      await fetchVendors();
    } catch (err: any) {
      setSaveError(err.message || 'Failed to add vendor');
    } finally {
      setSaving(false);
    }
  };

  const handleScrambleAll = async () => {
    setScramblingAll(true);
    await new Promise(r => setTimeout(r, 1200));
    setScramblingAll(false);
    setScrambledAll(true);
    setShowScrambleAllModal(false);
  };

  const handleScramble = (id: string) => {
    // Individual scramble tracking
  };

  const filtered = vendors.filter(v => filter === 'all' || v.status === filter);
  const availableVendors = vendors.filter(v => v.status === 'available');

  const stats = [
    { label: 'Total Vendors', value: loading ? '—' : vendors.length, color: '#1E90FF', icon: 'BuildingOffice2Icon' },
    { label: 'Available Now', value: loading ? '—' : vendors.filter(v => v.status === 'available').length, color: '#00D68F', icon: 'CheckBadgeIcon' },
    { label: 'No Response', value: loading ? '—' : vendors.filter(v => v.status === 'no-response').length, color: '#FF4757', icon: 'ExclamationTriangleIcon' },
    { label: 'Blacklisted', value: loading ? '—' : vendors.filter(v => v.status === 'blacklisted').length, color: '#4A5568', icon: 'NoSymbolIcon' },
  ];

  const leaderboard = [...vendors]
    .filter(v => v.status !== 'blacklisted')
    .sort((a, b) => (b.winRate + b.responseSpeed * 5) - (a.winRate + a.responseSpeed * 5))
    .slice(0, 3)
    .map((v, i) => ({ rank: i + 1, name: v.name, score: Math.round(v.winRate * 0.7 + v.responseSpeed * 6), badge: ['🏆', '🥈', '🥉'][i] }));

  const inputStyle: React.CSSProperties = {
    background: 'var(--card)',
    border: '1px solid rgba(30,144,255,0.2)',
    borderRadius: 8,
    padding: '8px 12px',
    color: 'var(--card-foreground)',
    fontSize: 12,
    outline: 'none',
    width: '100%',
  };

  return (
    <AppLayout pageTitle="Vendor Command">
      <div className="flex items-start justify-between mb-5">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <span className="text-[10px] font-700 tracking-widest uppercase px-2 py-0.5 rounded" style={{ background: 'rgba(245,158,11,0.15)', color: '#F59E0B', border: '1px solid rgba(245,158,11,0.3)' }}>◆ VENDOR COMMAND</span>
          </div>
          <h1 className="page-title" style={{ color: 'var(--card-foreground)' }}>Scramble Panel</h1>
          <p className="text-sm mt-1" style={{ color: 'var(--muted-foreground)' }}>Operator profiles · One-click RFQ scramble · Response radar</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowScrambleAllModal(true)}
            disabled={scrambledAll || availableVendors.length === 0}
            className="btn-amber text-sm flex items-center gap-1.5"
          >
            <Icon name="BoltIcon" size={14} />{scrambledAll ? '✈️ All Scrambled' : 'Scramble All Available'}
          </button>
          <button onClick={() => { setShowAddModal(true); setSaveError(''); setFormData(EMPTY_VENDOR); }} className="btn-ghost text-sm flex items-center gap-1.5">
            <Icon name="PlusIcon" size={14} />Add Vendor
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
        {stats.map(s => (
          <div key={s.label} className="rounded-xl p-4 flex items-center gap-3" style={{ background: 'var(--card)', border: '1px solid rgba(30,144,255,0.15)' }}>
            <div className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0" style={{ background: `${s.color}15`, border: `1px solid ${s.color}30` }}>
              <Icon name={s.icon as any} size={17} style={{ color: s.color } as React.CSSProperties} />
            </div>
            <div>
              <p className="text-xl font-700 font-tabular" style={{ color: 'var(--card-foreground)' }}>{s.value}</p>
              <p className="text-[10px]" style={{ color: 'var(--muted-foreground)' }}>{s.label}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Leaderboard */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-5">
        <div className="md:col-span-1 rounded-xl p-4" style={{ background: 'var(--card)', border: '1px solid rgba(245,158,11,0.2)' }}>
          <p className="text-xs font-700 uppercase tracking-widest mb-3" style={{ color: '#F59E0B' }}>🏆 Top Performers</p>
          <div className="space-y-2">
            {leaderboard.map(l => (
              <div key={l.rank} className="flex items-center gap-2 p-2 rounded-lg" style={{ background: 'rgba(245,158,11,0.06)' }}>
                <span className="text-base">{l.badge}</span>
                <span className="text-xs font-600 flex-1 truncate" style={{ color: 'var(--card-foreground)' }}>{l.name}</span>
                <span className="text-xs font-700" style={{ color: '#F59E0B' }}>{l.score}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="md:col-span-2 rounded-xl p-4" style={{ background: 'var(--card)', border: '1px solid rgba(30,144,255,0.15)' }}>
          <p className="text-xs font-700 uppercase tracking-widest mb-3" style={{ color: 'var(--muted-foreground)' }}>Filter by Status</p>
          <div className="flex flex-wrap gap-2">
            {(['all', 'available', 'busy', 'no-response'] as const).map(f => (
              <button key={f} onClick={() => setFilter(f)} className="px-3 py-1.5 text-xs font-600 rounded-lg transition-all capitalize" style={{ background: filter === f ? 'rgba(30,144,255,0.15)' : 'rgba(30,144,255,0.05)', color: filter === f ? '#1E90FF' : 'var(--muted-foreground)', border: `1px solid ${filter === f ? 'rgba(30,144,255,0.3)' : 'rgba(30,144,255,0.1)'}` }}>
                {f === 'all' ? 'All Vendors' : f === 'no-response' ? 'No Response' : f.charAt(0).toUpperCase() + f.slice(1)}
                <span className="ml-1.5 text-[9px] font-700 px-1 py-0.5 rounded" style={{ background: 'rgba(30,144,255,0.15)', color: '#1E90FF' }}>
                  {f === 'all' ? vendors.length : vendors.filter(v => v.status === f).length}
                </span>
              </button>
            ))}
          </div>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-40">
          <div className="w-7 h-7 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
          {filtered.map(v => <VendorCard key={v.id} vendor={v} onScramble={handleScramble} />)}
          {filtered.length === 0 && (
            <div className="col-span-full text-center py-12" style={{ color: 'var(--muted-foreground)' }}>
              <Icon name="BuildingOffice2Icon" size={32} className="mx-auto mb-3 opacity-30" />
              <p className="text-sm">No vendors found</p>
            </div>
          )}
        </div>
      )}

      {/* Add Vendor Modal */}
      <Modal open={showAddModal} onClose={() => setShowAddModal(false)} title="Add New Vendor" size="md">
        <div className="space-y-4">
          <div>
            <label className="text-xs font-600 mb-1.5 block" style={{ color: 'var(--muted-foreground)' }}>Vendor / Company Name *</label>
            <input type="text" placeholder="e.g. Cisco Systems" value={formData.name} onChange={e => setFormData(p => ({ ...p, name: e.target.value }))} style={inputStyle} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-600 mb-1.5 block" style={{ color: 'var(--muted-foreground)' }}>Category</label>
              <select value={formData.category} onChange={e => setFormData(p => ({ ...p, category: e.target.value }))} style={inputStyle}>
                {['Networking', 'Security', 'Hardware', 'Software', 'Cloud', 'Cabling', 'Power', 'Other'].map(c => <option key={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs font-600 mb-1.5 block" style={{ color: 'var(--muted-foreground)' }}>Status</label>
              <select value={formData.status} onChange={e => setFormData(p => ({ ...p, status: e.target.value }))} style={inputStyle}>
                <option value="available">Available</option>
                <option value="busy">Busy</option>
                <option value="no-response">No Response</option>
              </select>
            </div>
          </div>
          <div>
            <label className="text-xs font-600 mb-1.5 block" style={{ color: 'var(--muted-foreground)' }}>Location</label>
            <input type="text" placeholder="e.g. Dubai, UAE" value={formData.location} onChange={e => setFormData(p => ({ ...p, location: e.target.value }))} style={inputStyle} />
          </div>
          <div>
            <label className="text-xs font-600 mb-1.5 block" style={{ color: 'var(--muted-foreground)' }}>Specialties (comma-separated)</label>
            <input type="text" placeholder="e.g. Routing, Switching, Firewalls" value={formData.specialties} onChange={e => setFormData(p => ({ ...p, specialties: e.target.value }))} style={inputStyle} />
          </div>
          {saveError && (
            <p className="text-xs px-3 py-2 rounded-lg" style={{ background: 'rgba(255,71,87,0.1)', color: '#FF4757', border: '1px solid rgba(255,71,87,0.2)' }}>{saveError}</p>
          )}
          <div className="flex gap-3 pt-1">
            <button onClick={handleAddVendor} disabled={saving} className="btn-primary text-sm px-5 py-2.5 flex items-center gap-2">
              <Icon name="PlusIcon" size={15} />{saving ? 'Adding...' : 'Add Vendor'}
            </button>
            <button onClick={() => setShowAddModal(false)} className="btn-secondary text-sm px-5 py-2.5">Cancel</button>
          </div>
        </div>
      </Modal>

      {/* Scramble All Confirmation Modal */}
      <Modal open={showScrambleAllModal} onClose={() => setShowScrambleAllModal(false)} title="Scramble All Available Vendors" size="sm">
        <div className="space-y-4">
          <div className="p-4 rounded-xl text-center" style={{ background: 'rgba(245,158,11,0.06)', border: '1px solid rgba(245,158,11,0.2)' }}>
            <p className="text-3xl mb-2">⚡</p>
            <p className="text-sm font-700" style={{ color: 'var(--card-foreground)' }}>{availableVendors.length} Available Vendors</p>
            <p className="text-xs mt-1" style={{ color: 'var(--muted-foreground)' }}>RFQ will be sent to all available vendors</p>
          </div>
          <div className="space-y-1.5 max-h-32 overflow-y-auto">
            {availableVendors.map(v => (
              <div key={v.id} className="flex items-center gap-2 px-3 py-1.5 rounded-lg" style={{ background: 'rgba(0,214,143,0.06)' }}>
                <span className="w-5 h-5 rounded-full flex items-center justify-center text-white text-[9px] font-700 shrink-0" style={{ background: 'linear-gradient(135deg, #1E90FF, #0066CC)' }}>{v.initials}</span>
                <span className="text-xs font-500" style={{ color: 'var(--card-foreground)' }}>{v.name}</span>
              </div>
            ))}
          </div>
          <div className="flex gap-3">
            <button onClick={handleScrambleAll} disabled={scramblingAll} className="btn-amber text-sm px-5 py-2.5 flex-1 flex items-center justify-center gap-2">
              <Icon name="BoltIcon" size={15} />{scramblingAll ? 'Scrambling...' : 'Confirm Scramble'}
            </button>
            <button onClick={() => setShowScrambleAllModal(false)} className="btn-secondary text-sm px-4 py-2.5">Cancel</button>
          </div>
        </div>
      </Modal>
    </AppLayout>
  );
}